import { zodResolver } from '@hookform/resolvers/zod'
import { CheckIcon } from '@phosphor-icons/react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { TextAreaFormField } from '@/components/form/text-area-form-field'
import { TextInputFormField } from '@/components/form/text-input-form-field'
import { Button } from '@/components/ui/button'

import { NoteFormData, NoteFormSchema } from '../reservation-details.types'
import * as S from '../reservations-details.styled'

interface Props {
  note?: NoteFormData
  onSubmitForm: (data: NoteFormData) => void
  closeForm: () => void
  shouldClearFormOnCancelClick?: boolean
}

export const NoteForm = ({
  note,
  onSubmitForm,
  closeForm,
  shouldClearFormOnCancelClick,
}: Props) => {
  const { t } = useTranslation(['reservations'])

  const form = useForm<NoteFormData>({
    defaultValues: note,
    mode: 'onSubmit',
    resolver: zodResolver(NoteFormSchema),
  })

  const isSaveDisabled =
    form.formState.isSubmitting || !form.watch('noteBody')?.trim() || !form.watch('title')?.trim()

  const onSubmit = (data: NoteFormData) => {
    onSubmitForm(data)
  }

  const handleCancelClick = () => {
    if (shouldClearFormOnCancelClick) {
      form.reset()
    } else {
      closeForm()
    }
  }

  return (
    <>
      <S.Note $noBackground>
        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <TextInputFormField
              label={t('reservations:details.notes.noteForm.title')}
              name="title"
              placeholder={t('reservations:details.notes.noteForm.titlePlaceholder')}
              characterLimit={200}
            />
            <TextAreaFormField
              label={t('reservations:details.notes.noteForm.noteBody')}
              name="noteBody"
              placeholder={t('reservations:details.notes.noteForm.noteBodyPlaceholder')}
              characterLimit={260}
            />
            <S.FormButtonsContainer>
              <Button use="secondary" size="small" onClick={handleCancelClick}>
                {t('reservations:details.notes.noteForm.cancel')}
              </Button>
              <Button use="primary" size="small" type="submit" disabled={isSaveDisabled}>
                {t('reservations:details.notes.noteForm.saveNote')}
                <CheckIcon />
              </Button>
            </S.FormButtonsContainer>
          </form>
        </FormProvider>
      </S.Note>
    </>
  )
}
